import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, apiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Film, Download, Loader2, Lock, Calendar, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export default function GuestPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [protectedPage, setProtectedPage] = useState(false);
  const [eventName, setEventName] = useState("");
  const [password, setPassword] = useState("");
  const [gt, setGt] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    axios.get(`${API}/guest/${token}`)
      .then((r) => {
        if (r.data.protected) { setProtectedPage(true); setEventName(r.data.event_name || ""); }
        else setData(r.data);
      })
      .catch((e) => setError(apiError(e, "This link is not available.")))
      .finally(() => setLoading(false));
  }, [token]);

  const verify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const { data: d } = await axios.post(`${API}/guest/${token}/access`, { password });
      setData(d); setGt(d.guest_token || ""); setProtectedPage(false);
    } catch (err) { toast.error(apiError(err, "Incorrect password.")); }
    finally { setVerifying(false); }
  };

  const mediaSrc = (o, download) => `${API}/guest/${token}/output/${o.id}/file?${gt ? `gt=${gt}&` : ""}${download ? "download=1" : ""}`;
  const posterSrc = (o) => `${API}/guest/${token}/output/${o.id}/poster?${gt ? `gt=${gt}` : ""}`;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-ivory"><Loader2 className="w-8 h-8 animate-spin" style={{ color: "#F7941E" }} /></div>;

  if (error)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-ivory px-6 text-center">
        <Film className="w-12 h-12 mb-4" style={{ color: "#F7941E" }} />
        <h1 className="font-display text-3xl" style={{ color: "#0E3E7E" }}>Link unavailable</h1>
        <p className="text-slate-500 mt-2 max-w-sm">{error}</p>
      </div>
    );

  if (protectedPage)
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory px-6">
        <form onSubmit={verify} className="w-full max-w-sm text-center">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-5" style={{ background: "#0E3E7E" }}>
            <Lock className="w-7 h-7" style={{ color: "#F7941E" }} />
          </div>
          <h1 className="font-display text-3xl mb-1" style={{ color: "#0E3E7E" }}>{eventName || "Private highlights"}</h1>
          <p className="text-slate-500 text-sm mb-6">This page is password protected. Please enter the password shared with you.</p>
          <Input data-testid="guest-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            className="h-12 text-center bg-white border-[#CFE7F2] text-[#0E3E7E]" />
          <Button data-testid="guest-password-submit" type="submit" disabled={verifying}
            className="w-full h-12 mt-4 rounded-full font-semibold" style={{ background: "#0E3E7E", color: "#FDFBF7" }}>
            {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : "View Highlights"}
          </Button>
        </form>
      </div>
    );

  const c1 = data.primary_color || "#0E3E7E";
  const c2 = data.secondary_color || "#F7941E";

  return (
    <div className="min-h-screen bg-ivory" style={{ color: "#0E3E7E" }}>
      {/* Hero */}
      <header className="relative text-center px-6 pt-16 pb-10">
        <div className="mb-6 flex justify-center">
          <Logo height={72} />
        </div>
        <p className="text-sm uppercase tracking-[0.2em] text-slate-400 mb-3">{data.event_name}</p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none">{data.client_name || data.event_name}</h1>
        <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-sm text-slate-500">
          {data.event_date && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" style={{ color: c2 }} /> {formatDate(data.event_date)}</span>}
          {data.venue && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" style={{ color: c2 }} /> {data.venue}</span>}
        </div>
        {data.message && <p className="max-w-xl mx-auto mt-8 text-lg text-slate-600 font-display italic">{data.message}</p>}
        {data.expiry && (
          <p className="mt-6 inline-flex items-center gap-1.5 text-xs text-slate-400 bg-white border border-[#CFE7F2] rounded-full px-3 py-1.5">
            <Clock className="w-3.5 h-3.5" /> Available until {formatDate(data.expiry)}
          </p>
        )}
        {data.sharing_status !== "social_ok" && (
          <div data-testid="guest-private-note" className="mt-4 flex justify-center">
            <p className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-white border border-[#CFE7F2] rounded-full px-3.5 py-1.5">
              <Lock className="w-3.5 h-3.5" style={{ color: c2 }} /> Shared privately for you — please keep these highlights personal
            </p>
          </div>
        )}
      </header>

      {/* Media */}
      <main className="max-w-3xl mx-auto px-6 pb-20 space-y-12">
        {data.outputs.length === 0 ? (
          <p className="text-center text-slate-400 py-10">Your highlights will appear here soon.</p>
        ) : (
          data.outputs.map((o, i) => (
            <div key={o.id} data-testid={`guest-output-${o.id}`} className="animate-fade-up">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-2xl">{o.name}</h2>
                {data.downloads_enabled && (
                  <a data-testid={`guest-download-${o.id}`} href={mediaSrc(o, true)} target="_blank" rel="noreferrer">
                    <Button size="sm" className="rounded-full font-medium" style={{ background: c1, color: "#FDFBF7" }}>
                      <Download className="w-4 h-4 mr-1.5" /> Download
                    </Button>
                  </a>
                )}
              </div>
              <div className="rounded-2xl overflow-hidden bg-white border border-[#CFE7F2] shadow-[0_8px_30px_rgb(0,0,0,0.06)] flex items-center justify-center"
                style={{ aspectRatio: `${o.width}/${o.height}`, maxHeight: "80vh" }}>
                {o.type === "gif" ? (
                  <img src={mediaSrc(o)} alt={o.name} className="w-full h-full object-contain" />
                ) : (
                  <video data-testid={`guest-video-${o.id}`} src={mediaSrc(o)} poster={posterSrc(o)} controls playsInline className="w-full h-full object-contain bg-black" />
                )}
              </div>
            </div>
          ))
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#CFE7F2] py-10 text-center">
        <div className="flex justify-center mb-3">
          <Logo height={44} />
        </div>
        {data.website && (
          <div>
            <a href={data.website.startsWith("http") ? data.website : `https://${data.website}`} target="_blank" rel="noreferrer"
              className="text-sm" style={{ color: c2 }}>{data.website}</a>
          </div>
        )}
        {!data.downloads_enabled && <p className="text-xs text-slate-400 mt-3">Downloads are disabled for this link.</p>}
      </footer>
    </div>
  );
}
